import React, { useState, useEffect } from 'react'
import { Link, Plus, Trash2, Settings, ExternalLink, Copy, Check, AlertCircle, Power, PowerOff } from 'lucide-react'
import { integrationsService } from '../../services/integrations'
import type { CrispIntegration, CreateCrispIntegrationRequest } from '../../services/integrations'
import { chatbotsService } from '../../services/chatbots'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { useToast } from '../../hooks/useToast'

export default function Integrations() {
  const [integrations, setIntegrations] = useState<CrispIntegration[]>([])
  const [chatbots, setChatbots] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedIntegration, setSelectedIntegration] = useState<CrispIntegration | null>(null)
  const [copiedWebhookId, setCopiedWebhookId] = useState<string | null>(null)

  // Create form state
  const [chatbotId, setChatbotId] = useState('')
  const [websiteId, setWebsiteId] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [autoRespond, setAutoRespond] = useState(true)
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.7)
  const [autoHandoff, setAutoHandoff] = useState(true)
  const [creating, setCreating] = useState(false)

  const { toast } = useToast()

  // Load integrations and chatbots
  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [integrationsData, chatbotsResponse] = await Promise.all([
        integrationsService.listCrispIntegrations(),
        chatbotsService.getChatbots(1, 100) // Get first 100 chatbots
      ])
      console.log('Integrations loaded:', integrationsData)
      console.log('Chatbots response:', chatbotsResponse)
      console.log('Chatbots items:', chatbotsResponse.items)
      setIntegrations(integrationsData)
      setChatbots(chatbotsResponse.items || [])
    } catch (error) {
      console.error('Failed to load data:', error)
      toast({ title: 'Error', description: 'Failed to load integrations', variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async () => {
    if (!chatbotId || !websiteId || !identifier || !apiKey) {
      toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' })
      return
    }

    try {
      setCreating(true)
      const body: CreateCrispIntegrationRequest = {
        chatbot_id: chatbotId,
        website_id: websiteId,
        identifier,
        key: apiKey,
        auto_respond: autoRespond,
        confidence_threshold: confidenceThreshold,
        auto_handoff_on_low_confidence: autoHandoff
      }

      const newIntegration = await integrationsService.createCrispIntegration(body)
      setIntegrations([...integrations, newIntegration])
      setShowCreateModal(false)
      resetForm()
      toast({ title: 'Success', description: 'Crisp integration created successfully' })
    } catch (error: any) {
      console.error('Failed to create integration:', error)
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create integration', variant: 'destructive' })
    } finally {
      setCreating(false)
    }
  }

  const handleToggleActive = async (integration: CrispIntegration) => {
    try {
      const updated = await integrationsService.updateCrispIntegration(integration.id, {
        is_active: !integration.is_active
      })
      setIntegrations(integrations.map(i => i.id === updated.id ? updated : i))
      toast({ title: 'Success', description: `Integration ${updated.is_active ? 'enabled' : 'disabled'}` })
    } catch (error) {
      console.error('Failed to toggle integration:', error)
      toast({ title: 'Error', description: 'Failed to update integration', variant: 'destructive' })
    }
  }

  const handleDelete = async () => {
    if (!selectedIntegration) return

    try {
      await integrationsService.deleteCrispIntegration(selectedIntegration.id)
      setIntegrations(integrations.filter(i => i.id !== selectedIntegration.id))
      setShowDeleteModal(false)
      setSelectedIntegration(null)
      toast({ title: 'Success', description: 'Integration deleted successfully' })
    } catch (error) {
      console.error('Failed to delete integration:', error)
      toast({ title: 'Error', description: 'Failed to delete integration', variant: 'destructive' })
    }
  }

  const copyWebhookUrl = async (integration: CrispIntegration) => {
    try {
      await navigator.clipboard.writeText(integration.webhook_url)
      setCopiedWebhookId(integration.id)
      toast({ title: 'Success', description: 'Webhook URL copied to clipboard' })
      setTimeout(() => setCopiedWebhookId(null), 2000)
    } catch (error) {
      console.error('Failed to copy webhook URL:', error)
      toast({ title: 'Error', description: 'Failed to copy webhook URL', variant: 'destructive' })
    }
  }

  const resetForm = () => {
    setChatbotId('')
    setWebsiteId('')
    setIdentifier('')
    setApiKey('')
    setAutoRespond(true)
    setConfidenceThreshold(0.7)
    setAutoHandoff(true)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">Integrations</h1>
        <p className="text-muted-foreground">Connect your CleverAct AI chatbots with third-party platforms</p>
      </div>

      {/* Crisp Integration Section */}
      <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Link className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">Crisp Chat</h2>
                <p className="text-sm text-muted-foreground">Add AI-powered responses to your Crisp conversations</p>
              </div>
            </div>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Integration
          </Button>
        </div>

        {/* Integrations List */}
        {integrations.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
            <Link className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Crisp integrations yet</h3>
            <p className="text-muted-foreground mb-4">Connect your Crisp website to start using AI responses</p>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Integration
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {integrations.map(integration => (
              <div
                key={integration.id}
                className="border border-border rounded-lg p-4 hover:border-border transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-foreground">{integration.chatbot_name}</h3>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          integration.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {integration.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      Website ID: <code className="bg-muted px-2 py-0.5 rounded text-xs">{integration.website_id}</code>
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(integration)}
                      className={`p-2 rounded-lg transition-colors ${
                        integration.is_active
                          ? 'hover:bg-red-50 text-green-600 hover:text-red-600'
                          : 'hover:bg-green-50 text-muted-foreground hover:text-green-600'
                      }`}
                      title={integration.is_active ? 'Disable' : 'Enable'}
                    >
                      {integration.is_active ? <Power className="w-5 h-5" /> : <PowerOff className="w-5 h-5" />}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedIntegration(integration)
                        setShowDeleteModal(true)
                      }}
                      className="p-2 hover:bg-red-50 text-muted-foreground hover:text-red-600 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Statistics */}
                <div className="grid grid-cols-3 gap-4 mb-3">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-blue-600">{integration.messages_received}</div>
                    <div className="text-xs text-blue-600">Messages Received</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-green-600">{integration.messages_sent}</div>
                    <div className="text-xs text-green-600">AI Responses Sent</div>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-orange-600">{integration.handoffs_triggered}</div>
                    <div className="text-xs text-orange-600">Human Handoffs</div>
                  </div>
                </div>

                {/* Configuration */}
                <div className="bg-muted rounded-lg p-3 mb-3">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div>
                      <span className="font-medium">Auto-respond:</span>{' '}
                      <span className={integration.auto_respond ? 'text-green-600' : 'text-muted-foreground'}>
                        {integration.auto_respond ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Confidence threshold:</span>{' '}
                      {(integration.confidence_threshold * 100).toFixed(0)}%
                    </div>
                    <div>
                      <span className="font-medium">Auto-handoff:</span>{' '}
                      <span className={integration.auto_handoff_on_low_confidence ? 'text-green-600' : 'text-muted-foreground'}>
                        {integration.auto_handoff_on_low_confidence ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Webhook URL */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start gap-2 mb-2">
                    <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-yellow-900 mb-1">Webhook Configuration Required</p>
                      <p className="text-xs text-yellow-700 mb-2">
                        Add this webhook URL to your Crisp settings and subscribe to: <strong>message:send</strong>, <strong>session:request:initiated</strong>
                      </p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-white px-3 py-2 rounded text-xs text-foreground border border-yellow-200 break-all">
                          {integration.webhook_url}
                        </code>
                        <button
                          onClick={() => copyWebhookUrl(integration)}
                          className="flex-shrink-0 p-2 hover:bg-yellow-100 rounded transition-colors"
                          title="Copy webhook URL"
                        >
                          {copiedWebhookId === integration.id ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4 text-yellow-700" />
                          )}
                        </button>
                        <a
                          href="https://help.crisp.chat/en/article/how-to-connect-webhooks-to-crisp-1p1cwq9/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 p-2 hover:bg-yellow-100 rounded transition-colors"
                          title="View Crisp webhook setup guide"
                        >
                          <ExternalLink className="w-4 h-4 text-yellow-700" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Integration Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false)
          resetForm()
        }}
        title="Add Crisp Integration"
        size="lg"
      >
        <div className="space-y-4 pb-4">
          {/* Chatbot Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Chatbot <span className="text-red-500">*</span>
            </label>
            <select
              value={chatbotId}
              onChange={e => setChatbotId(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Select a chatbot...</option>
              {chatbots.map(bot => (
                <option key={bot.id} value={bot.id}>
                  {bot.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">The AI chatbot that will handle Crisp conversations</p>
          </div>

          {/* Website ID */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Crisp Website ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={websiteId}
              onChange={e => setWebsiteId(e.target.value)}
              placeholder="e.g., 8c4g5d6f-7h8i-9j0k-1l2m-3n4o5p6q7r8s"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <p className="text-xs text-muted-foreground mt-1">Found in Crisp Settings → Website Settings</p>
          </div>

          {/* Identifier */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Plugin Identifier <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={identifier}
              onChange={e => setIdentifier(e.target.value)}
              placeholder="e.g., plugin-identifier"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <p className="text-xs text-muted-foreground mt-1">Your Crisp plugin/integration identifier</p>
          </div>

          {/* API Key */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              API Key <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder="Your Crisp API key"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <p className="text-xs text-muted-foreground mt-1">Generated in Crisp Settings → API</p>
          </div>

          {/* Settings */}
          <div className="border-t border-border pt-4">
            <h4 className="text-sm font-medium text-foreground mb-3">AI Behavior</h4>

            <div className="space-y-3">
              {/* Auto-respond */}
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={autoRespond}
                  onChange={e => setAutoRespond(e.target.checked)}
                  className="w-4 h-4 text-primary border-border rounded focus:ring-ring"
                />
                <div>
                  <div className="text-sm font-medium text-foreground">Auto-respond</div>
                  <div className="text-xs text-muted-foreground">Automatically send AI responses to customer messages</div>
                </div>
              </label>

              {/* Confidence Threshold */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Confidence Threshold: {(confidenceThreshold * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={confidenceThreshold}
                  onChange={e => setConfidenceThreshold(parseFloat(e.target.value))}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Minimum AI confidence required to send a response
                </p>
              </div>

              {/* Auto-handoff */}
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={autoHandoff}
                  onChange={e => setAutoHandoff(e.target.checked)}
                  className="w-4 h-4 text-primary border-border rounded focus:ring-ring"
                />
                <div>
                  <div className="text-sm font-medium text-foreground">Auto-handoff on low confidence</div>
                  <div className="text-xs text-muted-foreground">
                    Transfer to human agent when AI confidence is below threshold
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button
              onClick={() => {
                setShowCreateModal(false)
                resetForm()
              }}
              variant="secondary"
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !chatbotId || !websiteId || !identifier || !apiKey}
            >
              {creating ? 'Creating...' : 'Create Integration'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setSelectedIntegration(null)
        }}
        title="Delete Integration"
        size="sm"
      >
        <div className="pb-4">
          <div className="flex items-start gap-4 mb-6">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1 pt-1">
              <p className="text-base text-foreground mb-2">
                Are you sure you want to delete the integration for <strong>{selectedIntegration?.chatbot_name}</strong>?
              </p>
              <p className="text-sm text-muted-foreground mb-2">
                This will stop AI responses for Crisp website <code className="bg-muted px-1 rounded">{selectedIntegration?.website_id}</code>.
              </p>
              <p className="text-sm text-muted-foreground">
                Remember to remove the webhook from your Crisp settings.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              onClick={() => {
                setShowDeleteModal(false)
                setSelectedIntegration(null)
              }}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Integration
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
